import api from ".";
import AGENT_CONFIG from "../agent_configs";
import ENDPOINTS from "./endpoints";
import UserAuthStore from "../store/userStore";
import { groupMessagesByTurn } from "@photon/chat-core";

// Fetch Spectra activities
export const getSpectraActivities = async (orgId, spaceId, limit = 20, offset = 0) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.SPECTRA_ACTIVITIES(orgId, spaceId, limit, offset),
  });
  return response.data;
};

// Fetch Spectra sparks
export const getSpectraSparks = async (spaceId, orgId, userId, ownerId = null) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.SPECTRA_SPARKS(spaceId, orgId, userId, ownerId),
  });
  return response.data;
};

// Fetch single Spectra by ID
export const getSpectraById = async (spaceId, orgId, userId) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.SPECTRA_BY_ID(spaceId, orgId, userId),
  });
  return response.data;
};

// Fetch only the owner_id for a Spectra space (lightweight — no file/TOC data)
export const getSpectraOwner = async (spaceId, orgId, userId) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.SPECTRA_OWNER(spaceId, orgId, userId),
  });
  return response.data;
};

// Fetch comprehensive Spectra details (activities, sparks, collaborators, summary)
export const getSpectraDetails = async (spaceId, orgId, userId, ownerId, params = {}) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.SPECTRA_DETAILS(spaceId, orgId, userId, ownerId, params),
  });
  return response.data;
};

// Fetch chat messages
export const getChatMessages = async (chatId, space) => {
  const { user, orgId } = UserAuthStore.getState();
  
  const spaceId = space?.space_id || null;
  const ownerId = space?.owner_id || null;
  
  const response = await api({
    method: "GET",
    url: ENDPOINTS.GET_MESSAGES(chatId, orgId, user?.uid, spaceId, ownerId),
  });
  
  return {
    ...response.data,
    messages: groupMessagesByTurn(response.data?.messages || []),
  };
};

// Fetch agents using GET_AGENTS endpoint
export const getAgents = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.GET_AGENTS,
    data,
  });
  return response.data;
};
// ================= Model Preferences API =================

export const getModelPreferences = async (orgId, userId) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.GET_MODEL_PREFERENCES,
    params: { org_id: orgId, user_id: userId },
  });
  return response.data;
};

export const updateModelPreferences = async (orgId, userId, models) => {
  const response = await api({
    method: "PUT",
    url: ENDPOINTS.UPDATE_MODEL_PREFERENCES,
    data: { org_id: orgId, user_id: userId, models },
  });
  return response.data;
};
export const toggleModelEnabled = async (orgId, userId, modelId, enabled) => {
  const response = await api({
    method: "PATCH",
    url: `${ENDPOINTS.TOGGLE_MODEL}/${modelId}/toggle`,
    data: { org_id: orgId, user_id: userId, enabled },
  });
  return response.data;
};

export const setDefaultModel = async (orgId, userId, modelId) => {
  const response = await api({
    method: "POST",
    url: `${ENDPOINTS.SET_DEFAULT_MODEL}/${modelId}/default`,
    data: { org_id: orgId, user_id: userId },
  });
  return response.data;
};

export const updateModelPreference = async (orgId, userId, modelId, updates) => {
  const response = await api({
    method: "PATCH",
    url: `${ENDPOINTS.TOGGLE_MODEL}/${modelId}`,
    data: { org_id: orgId, user_id: userId, updates },
  });
  return response.data;
};

export const getModelDefaults = async (orgId) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.GET_MODEL_DEFAULTS,
    params: { org_id: orgId },
  });
  // Normalize keys to lowercase — backend stores uppercase (DEFAULT_IMAGE_MODEL)
  // but frontend reads lowercase (default_image_model)
  return Object.fromEntries(Object.entries(response.data).map(([k, v]) => [k.toLowerCase(), v]));
};

export const updateModelDefaults = async (orgId, userId, defaults) => {
  const response = await api({
    method: "PUT",
    url: ENDPOINTS.UPDATE_MODEL_DEFAULTS,
    data: { org_id: orgId, user_id: userId, defaults },
  });
  return response.data;
};

// ─── Model Provider Config ────────────────────────────────────────────────────

export const getModelProvider = async (orgId, modelProviderId) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.GET_MODEL_PROVIDER(modelProviderId),
    params: { org_id: orgId },
  });
  return response.data;
};

export const updateModelProvider = async (orgId, userId, modelProviderId, data) => {
  const response = await api({
    method: "PUT",
    url: ENDPOINTS.UPDATE_MODEL_PROVIDER(modelProviderId),
    data: { org_id: orgId, data },
  });
  return response.data;
};

export const deleteModelProvider = async (orgId, modelProviderId) => {
  const response = await api({
    method: "DELETE",
    url: ENDPOINTS.DELETE_MODEL_PROVIDER(modelProviderId),
    params: { org_id: orgId },
  });
  return response.data;
};

export const listModelProviders = async (orgId) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.LIST_MODEL_PROVIDERS,
    params: { org_id: orgId },
  });
  return response.data;
};


export const getAIModels = async () => {
  const { orgId } = UserAuthStore.getState();
  const response = await api({
    method: "GET",
    url: `${ENDPOINTS.GET_MODELS_ME}?org_id=${orgId}`,
  });
  
  // Fetch defaults to get default model ID
  const defaultsResponse = await api({
    method: "GET",
    url: `/model/defaults/get?org_id=${orgId}`,
  });
  const default_model_id = defaultsResponse.data.default_text_model;
  
  return response.data.models.map((model) => ({
    ...model,
    id: model.model_id, // Map model_id to id for frontend compatibility
    active: model.active ? "True" : "False", // Map boolean to string for compatibility
    company_name: model.company,
    display_name: model.model_name,
    name: model.model_name,
    is_default: model.model_id === default_model_id,
  }));
};

// Returns ALL models (including inactive) with full field data for admin config.
export const getAllModels = async (orgId) => {
  const response = await api({
    method: "GET",
    url: `${ENDPOINTS.GET_ALL_MODELS}?org_id=${orgId}&user_only=false`,
  });
  return response.data.models ?? [];
};

// Update a single model's configuration fields (admin only).
// updates: { TEMPERATURE: 0.7, MAX_OUTPUT_TOKENS: 8192, USAGE_MULTIPLIER: 1.0 }
export const updateModel = async (orgId, userId, modelId, updates, replace = false) => {
  const response = await api({
    method: "PUT",
    url: `${ENDPOINTS.UPDATE_MODEL}/${encodeURIComponent(modelId)}`,
    data: { org_id: orgId, user_id: userId, updates, replace },
  });
  return response.data;
};

export const updateModelOrder = async (orgId, userId, models) => {
  const results = await Promise.allSettled(
    models.map(({ model_id, sort_order }) =>
      updateModel(orgId, userId, model_id, { SORT_ORDER: sort_order })
    )
  );
  const failed = results.filter((result) => result.status === "rejected");
  if (failed.length) {
    throw new Error(`Failed to save order for ${failed.length} of ${models.length} models`);
  }
};

// Create a new model (admin only).
// modelData: lowercase keys — will be uppercased by the caller before sending.
export const createModel = async (orgId, userId, modelId, modelData) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.CREATE_MODEL,
    data: { model_id: modelId, model_data: modelData },
  });
  return response.data;
};

// Delete a model and scrub it from group policies (admin only).
export const deleteModel = async (orgId, userId, modelId) => {
  const response = await api({
    method: "DELETE",
    url: `${ENDPOINTS.DELETE_MODEL}/${encodeURIComponent(modelId)}`,
    data: { org_id: orgId, user_id: userId },
  });
  return response.data;
};

export const createSpectra = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.CREATE_SPECTRA,
    data: data,
  });

  return response.data;
};

export const checkTagAvailability = async (orgId, tag) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.CHECK_TAG(orgId, tag),
  });
  return response.data;
};

export const getUserTags = async (orgId, userId) => {
  const response = await api({
    method: "GET",
    url: ENDPOINTS.GET_USER_TAGS(orgId, userId),
  });
  return response.data;
};

export const moveChatAPI = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.MOVE_CHAT,
    data: data,
  });

  return response.data;
};

export const deleteSpectra = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.DELETE_SPECTRA,
    data: data,
  });

  return response.data;
};

export const leaveSpectra = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.LEAVE_SPECTRA,
    data: data,
  });

  return response.data;
};

export const editSpectra = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.EDIT_SPECTRA,
    data: data,
  });

  return response.data;
};

export const updateSparkTitle = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.UPDATE_SPARK_TITLE,
    data: data,
  });

  return response.data;
};

//to delete the message in the chat
export const deleteMessage = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.DELETE_MESSAGE,
    data: data,
  });

  return response.data;
};

export const addCollaborator = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.ADD_COLLABORATOR,
    data: data,
  });

  return response.data;
};

export const removeCollaborator = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.REMOVE_COLLABORATOR,
    data: data,
  });

  return response.data;
};

export const setChatModel = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.SET_CHAT_MODEL,
    data: data,
  });

  return response.data;
};

export const getFileData = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.GET_FILE_DATA,
    data: data,
  });

  return response.data;
};

export const qzAgentBuild = async (data) => {
  const response = await api({
    method: "POST",
    url: AGENT_CONFIG.QUANTUM_AGENT_BUILD(),
    data: {
      ...data,
    },
  });

  return response.data;
};

export const qzAgentAtomCreationAPI = async (data) => {
  const response = await api({
    method: "POST",
    url: AGENT_CONFIG.QUANTUM_AGENT_ATOM_CREATION(),
    data: data,
  });

  return response.data;
};

export const qzAgentAtomStatusAPI = async (data) => {
  const response = await api({
    method: "POST",
    url: AGENT_CONFIG.ATOM_STATUS(),
    data: data,
  });

  return response.data;
};

export const qzAgentBuildDetailsAPI = async (data) => {
  const response = await api({
    method: "POST",
    url: AGENT_CONFIG.QUANTUM_AGENT_BUILD_DETAILS(),
    data: {
      ...data,
    },
  });

  return response.data;
};

export const qzAgentGetTools = async (data) => {
  const response = await api({
    method: "POST",
    url: AGENT_CONFIG.QUANTUM_AGENT_GET_TOOLS(),
    data: {
      ...data,
    },
  });

  return response.data;
};

export const getWorkspaceDetails = async (data) => {
  const response = await api({
    method: "POST",
    url: AGENT_CONFIG.GET_WORKSPACE_DETAILS(),
    data: data,
  });

  return response.data;
};

export const deleteAllSparks = async (data) => {
  const response = await api({
    method: "POST",
    url: AGENT_CONFIG.DELETE_ALL_SPARKS(),
    data: data,
  });

  return response.data;
};

export const initAnalysisMessage = async (data) => {
  const response = await api({
    method: "POST",
    url: AGENT_CONFIG.INIT_ANALYSIS_MESSAGE(),
    data: data,
  });
  return response.data;
};

export const listAgents = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.LIST_AGENTS,
    data,
  });
  return response.data;
};

/**
 * Export a chat message to PDF or DOCX.
 * Mermaid diagrams are pre-rendered as PNG base64 strings by the frontend
 * and sent alongside the markdown content so the backend can embed them.
 *
 * @param {object} data
 * @param {string} data.content       - Raw markdown content of the message
 * @param {string} data.format        - "pdf" or "docx"
 * @param {object} data.diagrams      - Map of mermaid source text → PNG base64 data URL
 * @param {string} data.org_id
 * @param {string} data.user_id
 * @returns {Promise<{ download_url: string }>}
 */
export const exportMessage = async (data) => {
  const response = await api({
    method: "POST",
    url: ENDPOINTS.EXPORT_MESSAGE,
    data: data,
  });
  return response.data;
};
